// Components==============
import { faCheckCircle, faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReflectionAvatar from 'components/UI/ReflectionAvatar/ReflectionAvatar';
import { motion } from 'framer-motion';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import React from 'react';
import { useHistory } from 'react-router';
import { ThemeWithProjects } from 'typings';
import { framerMountAndScale } from 'utilities/FramerAnimations';
import { getSmartImage } from 'utilities/getSmartImage';
import './Card.module.scss';
import assessment from 'actions/assessment';
// =========================

export default function FeedbackCard({
  theme,
  student,
  assessmentId,
  isLocked,
  isAssessed,
}: {
  theme: ThemeWithProjects | (ThemeWithProjects & { isFinished: boolean });
  student?: UserEntity;
  assessmentId: number;
  assessment?: AssessmentEntity;
  isLocked?: boolean;
  isAssessed?: boolean;
}) {
  const translate = useTranslate();
  const history = useHistory();

  const userId = useRedux((state) => state.authentication?.user?.id);

  const date = theme?.created_at
    ? new Date(theme?.created_at?.split(' ')[0]).toLocaleDateString('nl')
    : null;

  const firstImage = theme?.attachments
    .filter((a) => a.attachment_type === 'image')
    .map((a) => JSON.parse(a.attachment_data).file_path)[0] as string;

  const isTeacher = usePermission(['Docent', 'super-admin', 'Extern']);
  const isFirstAssessor = assessment.first_assessor_id == userId;
  const isSecondAssessor = assessment.second_assessor_id == userId;

  function handleClick() {
    if (isLocked) return;
    return history.push(`/groei/beoordelen/${assessmentId}`);
  }

  const peerReflections = useRedux((state) => {
    const reflections = state.reflection?.entities.allIds.map(
      (id) => state.reflection?.entities.byId[id]
    );

    return (
      theme?.project?.process
        ?.map((process) =>
          reflections?.filter((r) => r?.project_process_id === process)
        )
        .filter((arr) => arr?.length)
        .flat()
        .filter((reflection) => reflection?.for_user?.id === userId)
        .map((reflection) => reflection?.user)
        // filter duplicates
        .filter((v, i, a) => a.findIndex((t) => t?.id === v?.id) === i)
    );
  });

  return (
    <motion.div
      {...framerMountAndScale}
      styleName={`wrapper ${isLocked ? 'locked' : ''}`}
      onClick={handleClick}
    >
      {isLocked && (
        <div styleName="overlay">
          <div styleName="info">
            <FontAwesomeIcon
              styleName="lock"
              icon={faLock}
              size="2x"
              color="white"
            />
            <div styleName="text">
              <p>Wordt beoordeeld</p>
              <span>
                {isSecondAssessor
                  ? 'Omdat de eerste beoordelaar nog niet beoordeeld heeft, kun je hier zolang niet in.'
                  : isFirstAssessor
                  ? 'Omdat je deze student al beoordeeld hebt, kun je niet meer ingaan op deze beoordeling.'
                  : 'Omdat dit project momenteel wordt beoordeeld kun je hier tijdelijk niet in.'}
              </span>
            </div>
          </div>
        </div>
      )}
      <div styleName="image">
        <img alt={theme?.name} src={getSmartImage(firstImage)} />
      </div>
      <div styleName="right">
        <div styleName="top">
          <h2>
            {isAssessed && (
              <>
                <FontAwesomeIcon styleName="assessed" icon={faCheckCircle} />{' '}
              </>
            )}
            {theme?.name}
          </h2>
        </div>
        <div styleName={`bottom ${isTeacher ? 'is-teacher' : ''}`}>
          {isTeacher ? (
            <>
              <p>{`${theme?.author.first_name} ${theme?.author.last_name}`}</p>
              <p>{date}</p>
              {student && (
                <p styleName="bottom-text">
                  {`${student.first_name} ${student.last_name}`}
                </p>
              )}
            </>
          ) : (
            <>
              {!!peerReflections?.length && (
                <>
                  <p styleName="peer-text">
                    {translate('reflection.terms.peer-reflections')}
                  </p>
                  <div styleName="container">
                    <div styleName="avatar-wrapper">
                      {peerReflections.map(
                        (user) =>
                          !!user && (
                            <ReflectionAvatar user={user} key={user.id} />
                          )
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}
